<template>
  <div class="orders-layout">
    <!-- 主内容区 -->
    <main class="orders-content">
      <!-- 标题和搜索区域 -->
      <div class="content-header">
        <div class="header-left">
          <h1 class="page-title">订单管理</h1>
        </div>
        <div class="search-controls">
          <div class="search-bar">
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="搜索订单编号、商品名称或收银员..."
              class="search-input"
              @keyup.enter="handleSearch"
            />
            <button class="search-button" @click="handleSearch">搜索</button>
          </div>
          <button class="reset-button" @click="resetFilters">重置筛选</button>
        </div>
      </div>

      <!-- 状态筛选 -->
      <div class="filter-section">
        <div class="filter-label">订单状态：</div>
        <div class="status-filters">
          <div
            v-for="status in orderStatuses"
            :key="status.id"
            :class="['status-filter', { active: selectedStatus === status.id }]"
            @click="selectStatus(status.id)"
          >
            {{ status.name }}
          </div>
        </div>
      </div>

      <!-- 订单统计 -->
      <div class="order-stats">
        <div class="stat-card">
          <div class="stat-label">今日订单</div>
          <div class="stat-value">{{ todayOrderCount }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">今日销售额</div>
          <div class="stat-value">¥{{ todaySalesAmount.toFixed(2) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">待处理订单</div>
          <div class="stat-value warning">{{ pendingOrderCount }}</div>
        </div>
      </div>

      <!-- 订单表格 -->
      <div class="orders-table-container">
        <table class="orders-table">
          <thead>
            <tr>
              <th>订单编号</th>
              <th>订单时间</th>
              <th>购买商品</th>
              <th>数量</th>
              <th class="sortable-header" @click="toggleAmountSort">
                订单金额
                <span class="sort-indicator">
                  <span v-if="amountSortOrder === 'asc'" class="sort-asc">↑</span>
                  <span v-else-if="amountSortOrder === 'desc'" class="sort-desc">↓</span>
                  <span v-else class="sort-none">↕</span>
                </span>
              </th>
              <th>收银员</th>
              <th>支付方式</th>
              <th>订单状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="9" class="loading-data">
                <div class="loading-spinner"></div>
                加载中...
              </td>
            </tr>
            <tr v-else-if="orders.length === 0">
              <td colspan="9" class="no-data">暂无订单数据</td>
            </tr>
            <tr v-for="order in paginatedOrders" :key="order.id" class="order-row" @click="showOrderDetail(order)">
              <td class="order-id">{{ order.orderNo }}</td>
              <td class="order-time">{{ formatTime(order.createTime) }}</td>
              <td class="order-items">
                <div class="items-list">
                  <div v-for="item in order.items.slice(0, 2)" :key="item.id" class="item-row">
                    <div class="item-info-small">
                      <img
                        v-if="item.product && item.product.cover_image"
                        :src="item.product.cover_image"
                        :alt="item.name"
                        class="item-image-small"
                      />
                      <span class="item-name">{{ item.name }}</span>
                      <span v-if="item.isGift" class="gift-tag-small">赠</span>
                    </div>
                    <span class="item-price">¥{{ item.price.toFixed(2) }}</span>
                  </div>
                  <div v-if="order.items.length > 2" class="more-items">
                    还有{{ order.items.length - 2 }}件商品...
                  </div>
                </div>
              </td>
              <td class="total-quantity">{{ getTotalQuantity(order.items) }}</td>
              <td class="order-amount">¥{{ order.totalAmount.toFixed(2) }}</td>
              <td class="cashier">{{ order.cashier }}</td>
              <td class="payment-method">
                <span :class="['payment-tag', getPaymentClass(order.paymentMethod)]">
                  {{ getPaymentMethodName(order.paymentMethod) }}
                </span>
              </td>
              <td class="order-status">
                <span :class="['status-tag', getStatusClass(order.status)]">
                  {{ getStatusName(order.status) }}
                </span>
              </td>
              <td class="order-actions">
                <button
                  v-if="order.status === 1"
                  class="refund-btn"
                  @click.stop="handleRefund(order)"
                  :disabled="loading"
                >
                  退款
                </button>
                <span v-else class="no-action">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="pagination">
        <div class="pagination-info">
          显示 {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, total) }}
          共 {{ total }} 条
        </div>
        <div class="pagination-controls">
          <button
            class="page-btn"
            :disabled="currentPage === 1 || loading"
            @click="changePage(currentPage - 1)"
          >
            上一页
          </button>
          <span class="page-number">{{ currentPage }} / {{ totalPages }}</span>
          <button
            class="page-btn"
            :disabled="currentPage === totalPages || loading"
            @click="changePage(currentPage + 1)"
          >
            下一页
          </button>
        </div>
      </div>
    </main>

    <!-- 订单详情弹窗 -->
    <div v-if="selectedOrder" class="order-detail-modal" @click="closeOrderDetail">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>订单详情 - {{ selectedOrder.orderNo }}</h3>
          <button class="close-btn" @click="closeOrderDetail">×</button>
        </div>
        <div class="modal-body">
          <div class="order-info">
            <div class="info-row">
              <span class="label">下单时间：</span>
              <span class="value">{{ formatTime(selectedOrder.createTime) }}</span>
            </div>
            <div class="info-row">
              <span class="label">收银员：</span>
              <span class="value">{{ selectedOrder.cashier }}</span>
            </div>
            <div class="info-row">
              <span class="label">支付方式：</span>
              <span class="value">{{ getPaymentMethodName(selectedOrder.paymentMethod) }}</span>
            </div>
            <div class="info-row">
              <span class="label">订单状态：</span>
              <span class="value">{{ getStatusName(selectedOrder.status) }}</span>
            </div>
            <div v-if="selectedOrder.remark" class="info-row">
              <span class="label">备注：</span>
              <span class="value">{{ selectedOrder.remark }}</span>
            </div>
          </div>
          <div class="order-items">
            <h4>商品明细</h4>
            <table class="items-table">
              <thead>
                <tr>
                  <th>商品名称</th>
                  <th>单价</th>
                  <th>数量</th>
                  <th>小计</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in selectedOrder.items" :key="item.id">
                  <td class="item-name">
                    <div class="item-info">
                      <img
                        v-if="item.product && item.product.cover_image"
                        :src="item.product.cover_image"
                        :alt="item.name"
                        class="item-image"
                      />
                      <div class="item-text">
                        {{ item.name }}
                        <span v-if="item.isGift" class="gift-tag">赠品</span>
                      </div>
                    </div>
                  </td>
                  <td class="item-price">¥{{ item.price.toFixed(2) }}</td>
                  <td class="item-quantity">{{ item.quantity }}</td>
                  <td class="item-total">¥{{ item.subtotal.toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="order-summary">
              <div v-if="selectedOrder.originalAmount && selectedOrder.originalAmount !== selectedOrder.totalAmount" class="summary-row">
                <span>商品总额：</span>
                <span>¥{{ selectedOrder.originalAmount.toFixed(2) }}</span>
              </div>
              <div class="summary-row total">
                <span>实付金额：</span>
                <span>¥{{ selectedOrder.totalAmount.toFixed(2) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 退款确认对话框 -->
    <RefundModal
      ref="refundModal"
      :visible="refundModalVisible"
      :order-info="refundOrderInfo"
      @confirm="handleRefundConfirm"
      @cancel="handleRefundCancel"
    />
  </div>
</template>

<script>
import { orderApi } from '../api/index.js'
import RefundModal from './RefundModal.vue'
import toast from '../utils/toast.js'

export default {
  name: 'OrdersView',
  components: {
    RefundModal
  },
  props: {
    userInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      searchKeyword: '',
      selectedStatus: 0,
      currentPage: 1,
      amountSortOrder: null, // null, 'asc', 'desc'
      pageSize: 15,
      selectedOrder: null,
      orderStatuses: [
        { id: 0, name: '全部' },
        { id: 1, name: '已支付' },
        { id: 2, name: '已退款' }
      ],
      paymentMethods: [
        { id: 1, name: '现金', class: 'cash' },
        { id: 2, name: '微信', class: 'wechat' },
        { id: 3, name: '支付宝', class: 'alipay' },
        { id: 4, name: '银行卡', class: 'card' }
      ],
      orders: [],
      total: 0,
      loading: false,
      todayStats: {
        todayOrderCount: 0,
        todaySalesAmount: 0.0,
        todayRefundAmount: 0.0
      },
      // 退款模态框相关
      refundModalVisible: false,
      refundOrderInfo: null
    }
  },
  computed: {
    paginatedOrders() {
      return this.orders
    },
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    },
    todayOrderCount() {
      return this.todayStats.todayOrderCount
    },
    todaySalesAmount() {
      return this.todayStats.todaySalesAmount
    },
    pendingOrderCount() {
      return 0
    }
  },
  methods: {
    async loadOrders() {
      try {
        this.loading = true

        const params = {
          page: this.currentPage,
          pageSize: this.pageSize
        }

        if (this.searchKeyword) {
          params.order_no = this.searchKeyword
        }

        if (this.selectedStatus !== 0) {
          params.order_status = this.selectedStatus
        }

        const response = await orderApi.getCashierOrderList(params)

        if (response.code === 0) {
          this.orders = response.data.list.map(order => ({
            id: order.id,
            orderNo: order.order_no,
            createTime: new Date(order.order_time),
            totalAmount: order.final_amount,
            originalAmount: order.original_amount,
            itemCount: order.order_items ? order.order_items.length : 0,
            cashier: order.member ? order.member.name : '未知',
            paymentMethod: order.payment_method || 1,
            status: order.order_status,
            paymentStatus: order.payment_status,
            remark: order.remark,
            memberId: order.member_id,
            items: order.order_items ? order.order_items.map(item => ({
              id: item.productId,
              name: item.productName,
              price: item.unitPrice,
              quantity: item.quantity,
              subtotal: item.subtotal,
              isGift: item.isGift,
              product: item.product
            })) : []
          }))

          this.total = response.data.total

          if (response.data.stats) {
            this.todayStats = {
              todayOrderCount: response.data.stats.today_order_count || 0,
              todaySalesAmount: response.data.stats.today_sales_amount || 0.0,
              todayRefundAmount: response.data.stats.today_refund_amount || 0.0
            }
          }
        } else {
          alert('获取订单列表失败：' + response.msg)
        }
      } catch (error) {
        console.error('加载订单数据失败:', error)
        alert('加载订单数据失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    toggleAmountSort() {
      if (this.amountSortOrder === null) {
        this.amountSortOrder = 'asc'
      } else if (this.amountSortOrder === 'asc') {
        this.amountSortOrder = 'desc'
      } else {
        this.amountSortOrder = null
      }
      this.currentPage = 1
      this.loadOrders()
    },
    selectStatus(statusId) {
      this.selectedStatus = statusId
      this.currentPage = 1
      this.loadOrders()
    },
    handleSearch() {
      if (!this.searchKeyword.trim()) {
        alert('请输入搜索关键词')
        return
      }
      this.currentPage = 1
      this.loadOrders()
    },
    resetFilters() {
      this.searchKeyword = ''
      this.selectedStatus = 0
      this.amountSortOrder = null
      this.currentPage = 1
      this.loadOrders()
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.loadOrders()
      }
    },
    getStatusName(statusId) {
      const status = this.orderStatuses.find(s => s.id === statusId)
      return status ? status.name : '未知'
    },
    getStatusClass(statusId) {
      const classes = {
        1: 'completed',  // 已支付
        2: 'refunded'   // 已退款
      }
      return classes[statusId] || 'unknown'
    },
    getPaymentMethodName(methodId) {
      const method = this.paymentMethods.find(m => m.id === methodId)
      return method ? method.name : '未知'
    },
    getPaymentClass(methodId) {
      const method = this.paymentMethods.find(m => m.id === methodId)
      return method ? method.class : 'unknown'
    },
    formatTime(time) {
      const date = new Date(time)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    showOrderDetail(order) {
      this.selectedOrder = order
    },
    closeOrderDetail() {
      this.selectedOrder = null
    },
    handleRefund(order) {
      this.refundOrderInfo = {
        orderNo: order.orderNo,
        amount: order.totalAmount,
        orderId: order.id
      }
      this.refundModalVisible = true
    },
    handleRefundCancel() {
      this.refundModalVisible = false
      this.refundOrderInfo = null
    },
    async handleRefundConfirm(refundReason) {
      if (!this.refundOrderInfo) return

      try {
        this.loading = true
        this.$refs.refundModal?.setLoading(true)

        const requestData = {
          order_id: this.refundOrderInfo.orderId
        }

        // 如果用户输入了退款原因，则添加到请求中
        if (refundReason) {
          requestData.remark = refundReason
        }

        const response = await orderApi.refundOrder(requestData)

        if (response.code === 0) {
          toast.success('退款成功！')
          this.refundModalVisible = false
          this.refundOrderInfo = null
          // 重新加载订单数据
          this.loadOrders()
        } else {
          toast.error('退款失败：' + response.msg)
        }
      } catch (error) {
        console.error('退款失败:', error)
        toast.error('退款失败，请稍后重试')
      } finally {
        this.loading = false
        this.$refs.refundModal?.setLoading(false)
      }
    },
    getTotalQuantity(items) {
      return items.reduce((total, item) => total + item.quantity, 0)
    }
  },
  mounted() {
    this.loadOrders()
  }
}
</script>

<style scoped>
.orders-layout {
  height: 100vh;
  background-color: #f5f5f5;
  font-family: 'Microsoft YaHei', sans-serif;
}

/* 主内容区 */
.orders-content {
  flex: 1;
  height: 100vh;
  padding: 20px;
  overflow-y: auto;
  background-color: white;
  box-sizing: border-box;
  min-width: 0;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
}

.search-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-input {
  border: 1px solid #d9d9d9;
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 14px;
  width: 350px;
  outline: none;
  transition: all 0.2s;
}

.search-input:focus {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24,144,255,0.2);
}

.search-button, .reset-button {
  background-color: #1890ff;
  color: white;
  border: none;
  height: 32px;
  padding: 0 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.search-button:hover, .reset-button:hover {
  background-color: #4096ff;
}

.reset-button {
  background-color: #f5f5f5;
  color: #666;
  border: 1px solid #d9d9d9;
}

.reset-button:hover {
  background-color: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
}

/* 筛选区域 */
.filter-section {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #fafafa;
  border-radius: 8px;
}

.filter-label {
  font-weight: 500;
  color: #333;
  margin-right: 15px;
  white-space: nowrap;
}

.status-filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.status-filter {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 16px;
  cursor: pointer;
  font-size: 12px;
  background-color: white;
  transition: all 0.2s;
  white-space: nowrap;
}

.status-filter:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.status-filter.active {
  background-color: #1890ff;
  border-color: #1890ff;
  color: white;
}

/* 统计卡片 */
.order-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.stat-card:nth-child(2) {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-card:nth-child(3) {
  background: linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%);
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
}

.stat-value.warning {
  color: #ffeb3b;
}

/* 表格 */
.orders-table-container {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  width: 100%;
}

.orders-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.orders-table th {
  background-color: #fafafa;
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e8e8e8;
  font-size: 14px;
  white-space: nowrap;
}

.orders-table th:nth-child(1) { width: 14%; }
.orders-table th:nth-child(2) { width: 14%; }
.orders-table th:nth-child(3) { width: 25%; }
.orders-table th:nth-child(4) { width: 8%; }
.orders-table th:nth-child(5) { width: 12%; }
.orders-table th:nth-child(6) { width: 10%; }
.orders-table th:nth-child(7) { width: 10%; }
.orders-table th:nth-child(8) { width: 7%; }
.orders-table th:nth-child(9) { width: 8%; }

.sortable-header {
  cursor: pointer;
  user-select: none;
  position: relative;
  transition: background-color 0.2s;
}

.sortable-header:hover {
  background-color: #f0f0f0;
}

.sort-indicator {
  margin-left: 8px;
  font-size: 12px;
  color: #666;
}

.sort-asc { color: #52c41a; }
.sort-desc { color: #f5222d; }
.sort-none { color: #999; }

.orders-table td {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
}

.order-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.order-row:hover {
  background-color: #f9f9f9;
}

.order-id {
  font-family: monospace;
  color: #1890ff;
  font-weight: 500;
}

.order-time {
  color: #666;
}

.order-items {
  padding: 8px 5px;
}

.items-list {
  min-height: 20px;
}

.item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3px;
  font-size: 12px;
}

.item-row:last-child {
  margin-bottom: 0;
}

.item-info-small {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.item-image-small {
  width: 24px;
  height: 24px;
  object-fit: cover;
  border-radius: 3px;
  border: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.item-name {
  color: #333;
  flex: 1;
  margin-right: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-price {
  color: #666;
  font-size: 11px;
  min-width: 35px;
  text-align: right;
  flex-shrink: 0;
}

.gift-tag-small {
  display: inline-block;
  background-color: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
  border-radius: 3px;
  padding: 1px 3px;
  font-size: 9px;
  margin-left: 4px;
  flex-shrink: 0;
}

.more-items {
  color: #999;
  font-size: 11px;
  font-style: italic;
  margin-top: 2px;
}

.total-quantity {
  text-align: center;
  font-weight: 600;
  color: #333;
}

.order-amount {
  text-align: right;
  font-weight: 600;
  color: #e74c3c;
}

.cashier {
  color: #333;
}

.payment-tag {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.payment-tag.cash { background-color: #e6f7ff; color: #1890ff; }
.payment-tag.wechat { background-color: #f6ffed; color: #52c41a; }
.payment-tag.alipay { background-color: #fff2e8; color: #fa8c16; }
.payment-tag.card { background-color: #f9f0ff; color: #722ed1; }

.status-tag {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.status-tag.completed { background-color: #f6ffed; color: #52c41a; }
.status-tag.pending { background-color: #fff7e6; color: #fa8c16; }
.status-tag.cancelled { background-color: #fff1f0; color: #f5222d; }
.status-tag.refunded { background-color: #f0f0f0; color: #666; }

/* 操作按钮 */
.order-actions {
  text-align: center;
}

.refund-btn {
  background-color: #ff4d4f;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.refund-btn:hover:not(:disabled) {
  background-color: #ff7875;
}

.refund-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.no-action {
  color: #999;
  font-size: 14px;
}

.no-data {
  text-align: center;
  color: #999;
  padding: 40px;
  font-size: 14px;
}

.loading-data {
  text-align: center;
  color: #666;
  padding: 40px;
  font-size: 14px;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 10px;
  vertical-align: middle;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 20px;
  padding: 20px;
  background-color: #fafafa;
  border-radius: 8px;
  box-sizing: border-box;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.page-btn {
  background-color: white;
  border: 1px solid #d9d9d9;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
}

.page-btn:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-number {
  font-weight: 500;
  color: #333;
}

/* 订单详情弹窗 */
.order-detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e8e8e8;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.order-info {
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  margin-bottom: 10px;
}

.info-row .label {
  width: 100px;
  color: #666;
}

.info-row .value {
  flex: 1;
  color: #333;
  font-weight: 500;
}

.order-items h4 {
  margin: 0 0 15px 0;
  color: #333;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.items-table th {
  background-color: #fafafa;
  padding: 10px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border: 1px solid #e8e8e8;
}

.items-table td {
  padding: 10px;
  border: 1px solid #e8e8e8;
}

.item-total {
  text-align: right;
  font-weight: 600;
  color: #e74c3c;
}

.gift-tag {
  display: inline-block;
  background-color: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 10px;
  margin-left: 8px;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.item-image {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.item-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.order-summary {
  border-top: 1px solid #e8e8e8;
  padding-top: 15px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.summary-row.total {
  font-weight: bold;
  font-size: 16px;
  color: #e74c3c;
}
</style>